import type React from "react";
import {useCallback, useMemo, useRef, useState} from "react";
import {
    Background,
    BackgroundVariant,
    Controls,
    getNodesBounds,
    type NodeMouseHandler,
    type NodeTypes,
    type OnInit,
    Panel,
    ReactFlow,
    type Viewport,
} from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import {Box, Stack} from "@mui/material";
import {COLORS} from "~/style/colors-ts";
import type {SupplierEntity} from "~/pages/supplier-360/types/Supplier360Types";
import type {ValueChainTree} from "~/pages/supplier-360/tabs/SupplierValueChain/valueChainLinkTypes";
import {
    buildValueChainGraph,
    type CounterpartyNode,
    parentIds,
    supplyTypeOptions,
    type ValueChainNode,
} from "~/pages/supplier-360/tabs/SupplierValueChain/components/chain/valueChainLayout";
import {ChainSupplyTypeFilter} from "~/pages/supplier-360/tabs/SupplierValueChain/components/chain/ChainSupplyTypeFilter";
import {ChainTierToggles} from "~/pages/supplier-360/tabs/SupplierValueChain/components/chain/ChainTierToggles";
import {ChainHoveredNodePanel} from "~/pages/supplier-360/tabs/SupplierValueChain/components/chain/ChainHoveredNodePanel";
import {ChainFocusNode} from "~/pages/supplier-360/tabs/SupplierValueChain/components/chain/ChainFocusNode";
import {ChainCounterpartyNode} from "~/pages/supplier-360/tabs/SupplierValueChain/components/chain/ChainCounterpartyNode";
import {ChainStubNode} from "~/pages/supplier-360/tabs/SupplierValueChain/components/chain/ChainStubNode";
import {ChainLaneNode} from "~/pages/supplier-360/tabs/SupplierValueChain/components/chain/ChainLaneNode";
import {DRAWER_HEADER_HEIGHT_LG} from "~/components/main/drawer/DrawerConstants.ts";

const NODE_TYPES: NodeTypes = {
    focus: ChainFocusNode,
    counterparty: ChainCounterpartyNode,
    stub: ChainStubNode,
    lane: ChainLaneNode,
};

const EDGE_STYLE: React.CSSProperties = {stroke: COLORS.grey[300], strokeWidth: 1.5};
const DIMMED_EDGE_STYLE: React.CSSProperties = {...EDGE_STYLE, opacity: 0.25};

/** The canvas never grows past this; a taller chart is fitted (and can be zoomed / panned) inside it. */
const MAX_CANVAS_HEIGHT = 560;
const CANVAS_PADDING = 48;

/** Fitting a long tier would shrink it to unreadable: the opening zoom stays in this range. */
const MIN_FIT_ZOOM = 0.6;
const MAX_FIT_ZOOM = 1;
const FIT_PADDING = 24;
/** Keeps the top / bottom cards clear of the filter panel and the zoom controls. */
const PANEL_CLEARANCE = 64;

type Bounds = {x: number; y: number; width: number; height: number};

/**
 * The opening view: the focus (centred on the layout's origin) in the middle of the canvas, zoomed
 * so the chain around it fits — but never below a readable zoom; pan for the rest. Worked out from
 * the known card sizes, so it doesn't wait for React Flow to measure anything.
 */
function openingViewport(bounds: Bounds, width: number, height: number): Viewport {
    // How far the chain reaches from the focus in each direction, since the focus stays centred.
    const reachX = Math.max(-bounds.x, bounds.x + bounds.width);
    const reachY = Math.max(-bounds.y, bounds.y + bounds.height);
    const fit = Math.min((width / 2 - FIT_PADDING) / reachX, (height / 2 - PANEL_CLEARANCE) / reachY);
    const zoom = Math.min(MAX_FIT_ZOOM, Math.max(MIN_FIT_ZOOM, fit));
    return {x: width / 2, y: height / 2, zoom};
}

type ValueChainBoardProps = {
    entity: SupplierEntity;
    tree: ValueChainTree;
};

/**
 * Upstream ← focus → downstream as a tidy tree, drawn with React Flow the way the taxonomy builder
 * draws a taxonomy: click a card to show or hide its own suppliers / buyers, or a tier's header for
 * the whole tier. Opens with only tier 1 shown and the focus in the middle; drag to pan, the
 * controls or Ctrl + scroll to zoom. Plain scrolling still scrolls the page.
 */
export const ValueChainBoard: React.FC<ValueChainBoardProps> = ({entity, tree}) => {
    // Opens with only tier 1: every card with a tier behind it starts collapsed.
    const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(() => new Set(parentIds(tree)));
    // A refetch can bring new tier-2+ branches (e.g. cached data first, fresh data after): start
    // those collapsed too, leaving whatever the user already opened or closed as it is.
    const [seenTree, setSeenTree] = useState(tree);
    // Bumped with each new tree, so the canvas reopens centred on the focus for it.
    const [treeVersion, setTreeVersion] = useState(0);
    if (seenTree !== tree) {
        const known = new Set(parentIds(seenTree));
        const added = parentIds(tree).filter(id => !known.has(id));
        setSeenTree(tree);
        setTreeVersion(version => version + 1);
        if (added.length > 0) setCollapsed(prev => new Set([...prev, ...added]));
    }
    const onToggleTier = useCallback((ids: string[], collapse: boolean) => {
        setCollapsed(prev => {
            const next = new Set(prev);
            ids.forEach(id => collapse ? next.add(id) : next.delete(id));
            return next;
        });
    }, []);

    const [selectedSupplyType, setSelectedSupplyType] = useState<string | null>(null);
    const supplyTypes = useMemo(() => supplyTypeOptions(tree), [tree]);
    // A type that's no longer in the data falls back to All.
    const supplyType = supplyTypes.some(option => option.code === selectedSupplyType) ? selectedSupplyType : null;

    const graph = useMemo(
        () => buildValueChainGraph(entity, tree, {collapsed, onToggleTier, supplyType}),
        [entity, tree, collapsed, onToggleTier, supplyType],
    );
    const edges = useMemo(
        () => graph.edges.map(edge => ({...edge, style: edge.data?.dimmed ? DIMMED_EDGE_STYLE : EDGE_STYLE})),
        [graph],
    );

    // Handlers keep their identity: React Flow hands them to every card, so a new one re-renders them all.
    const onNodeClick = useCallback<NodeMouseHandler<ValueChainNode>>((_, node) => {
        if (node.type !== 'counterparty' || node.data.childCount === 0) return;
        onToggleTier([node.id], !node.data.collapsed);
    }, [onToggleTier]);
    const [hoveredId, setHoveredId] = useState<string | null>(null);
    const onNodeMouseEnter = useCallback<NodeMouseHandler<ValueChainNode>>((_, node) => setHoveredId(node.id), []);
    const onNodeMouseLeave = useCallback(() => setHoveredId(null), []);
    const canvasRef = useRef<HTMLDivElement>(null);
    const onInit = useCallback<OnInit<ValueChainNode>>(instance => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        // Lane headers stay out of the fit: collapsed tiers' headers would otherwise widen it.
        void instance.setViewport(openingViewport(getNodesBounds(instance.getNodes().filter(node => node.type !== 'lane')), canvas.clientWidth, canvas.clientHeight));
    }, []);
    const hovered = graph.nodes.find((node): node is CounterpartyNode => node.id === hoveredId && node.type === 'counterparty');

    return (
        <Box
            ref={canvasRef}
            sx={{
                minHeight: `calc(100vh - ${DRAWER_HEADER_HEIGHT_LG}px - 63px - 50px - 16px - 16px)`,
                height: Math.min(graph.height + CANVAS_PADDING, MAX_CANVAS_HEIGHT),
                border: '1px solid',
                borderColor: 'grey.100',
                borderRadius: '12px',
                overflow: 'hidden',
            }}
        >
            <ReactFlow<ValueChainNode>
                // Remount on new data or a filter change so the opening view recentres on the focus.
                key={`${treeVersion}-${supplyType ?? "all"}`}
                nodes={graph.nodes}
                edges={edges}
                nodeTypes={NODE_TYPES}
                onNodeClick={onNodeClick}
                onNodeMouseEnter={onNodeMouseEnter}
                onNodeMouseLeave={onNodeMouseLeave}
                onInit={onInit}
                minZoom={0.1}
                maxZoom={2}
                nodesDraggable={false}
                nodesConnectable={false}
                nodesFocusable={false}
                edgesFocusable={false}
                elementsSelectable={false}
                zoomOnScroll={false}
                zoomActivationKeyCode={['Control', 'Meta']}
                zoomOnDoubleClick={false}
                preventScrolling={false}
                proOptions={{hideAttribution: true}}
            >
                <Background variant={BackgroundVariant.Dots} gap={20} size={1}/>
                {(graph.tierControls.length > 0 || supplyTypes.length > 0) && (
                    <Panel position="top-right">
                        <Stack direction="row" alignItems="flex-start" gap={1}>
                            {graph.tierControls.length > 0 && <ChainTierToggles controls={graph.tierControls}/>}
                            {supplyTypes.length > 0 && (
                                <ChainSupplyTypeFilter
                                    options={supplyTypes}
                                    value={supplyType}
                                    onChange={setSelectedSupplyType}
                                />
                            )}
                        </Stack>
                    </Panel>
                )}
                <ChainHoveredNodePanel node={hovered}/>
                <Controls showInteractive={false} position="bottom-right"/>
            </ReactFlow>
        </Box>
    );
};
